import React, { useState } from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopType from "@/enum/ShopType";
import {
    checkDecryptCaptchaRequired,
    listDecryptedConsignee,
    ListDecryptedConsigneeResult,
    postLogOrderDecryptConsigneeByPlatformApi,
} from "@/services/api/order";
import PddOrderDecryptor, { PddOrderDecryptResult } from "@/services/PddOrderDestpytor";
import ListDecryptedConsigneeError from "@/services/utils/ListDecryptedConsigneeError";
import { useGlobalStore } from "@/store/hooks";
import { useRequest } from "ahooks";
import { DecryptedType, getTbQnOrderDetailUrl } from "./useListDecryptedConsignee";
import { Input, Modal } from "antd";
import TBOrderDecryptor from "@/services/TBOrderDestpytor";

import type GlobalStore from "@/store";
import { getSETokenFromQn } from "@qn/services/api/getToken";
import { FD_BASE_URL } from "@/config";
import { showErrorModal } from "@/services/http/utils";
import { apiShopTypes } from "@/utils/ShopUtils";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import CaptchaImage from "@/components/CaptchaImage";
import Authority from "@/enum/Authority";

function tbOrderShouldDecrypt(order: OrderDTO) {
    const url = getTbQnOrderDetailUrl(order);

    window.open(url, "_blank");
}

interface CaptchaModalProps extends InjectBaseModalProps {
    shopId: number;
    onOk(captchaKey: string, captchaText: string): void;
    onReject?: () => void;
}

const CaptchaModal: React.FC<CaptchaModalProps> = ({ shopId, onOk, open, onCancel, onReject }) => {
    const [captchaKey, setCaptchaKey] = useState("");
    const [captchaText, setCaptchaText] = useState("");

    return (
        <Modal
            title="验证码"
            open={open}
            onCancel={() => {
                onReject();
                onCancel();
            }}
            onOk={() => {
                onOk(captchaKey, captchaText);
                onCancel();
            }}
            destroyOnHidden
        >
            <div style={{ display: "flex", alignItems: "center" }}>
                <Input
                    placeholder="请输入验证码"
                    value={captchaText}
                    onChange={(e) => setCaptchaText(e.target.value)}
                    style={{ marginRight: 10 }}
                />
                <CaptchaImage shopId={shopId} onChange={setCaptchaKey} />
            </div>
        </Modal>
    );
};

export async function singleOrderDecryptedConsignee(
    order: OrderDTO,
    type = DecryptedType.ALL,
    globalStore: typeof GlobalStore,
) {
    const shopId = order.shopId;

    const shop = globalStore.shopMapping.get(shopId);

    if (shop.type === ShopType.PINDUODUO) {
        const decryptor = new PddOrderDecryptor(order);

        const result = await decryptor.decrypt();

        if (!result.successful) {
            console.error("pdd 解密失败", result);

            const error = new ListDecryptedConsigneeError("", ShopType.PINDUODUO, result);

            if (result.isOriginalAddressDecryptForbidden && result.data) {
                handleOrderDecryptedConsigneeError(error, shopId);

                await postLogOrderDecryptConsigneeByPlatformApi(shopId, [
                    {
                        id: order.id,
                        originalId: order.originalId,
                        mergeSplitStatus: order.mergeSplitStatus,
                    },
                ]);

                return [
                    {
                        id: order.id,
                        consignee: {
                            ...result.data.consignee,
                            privacyProtection: result.data.privacyProtection,
                            virtualIdentifyNo: result.data.virtualIdentifyNo,
                        },
                    },
                ];
            }

            throw error;
        } else {
            await postLogOrderDecryptConsigneeByPlatformApi(shopId, [
                {
                    id: order.id,
                    originalId: order.originalId,
                    mergeSplitStatus: order.mergeSplitStatus,
                },
            ]);

            const listDecryptedConsigneeResult: ListDecryptedConsigneeResult[] = [
                {
                    id: order.id,
                    consignee: {
                        ...result.data.consignee,
                        privacyProtection: result.data.privacyProtection,
                        virtualIdentifyNo: result.data.virtualIdentifyNo,
                    },
                },
            ];

            return listDecryptedConsigneeResult;
        }
    } else if (shop.type === ShopType.TAOBAO) {
        if (type === DecryptedType.ALL) {
            return listDecryptedConsignee({
                shopId,
                orders: [order],
            });
        } else if (type === DecryptedType.PLATFORM) {
            const hasOtherShop = order.shopId !== globalStore.currentUser.shopId;

            if (hasOtherShop) {
                Modal.warning({
                    title:
                        "淘宝安全规范要求仅本店铺的账号登录才能查看订单隐私信息，请在商家后台登录此订单的淘宝店铺平台账号，再查看详细地址",
                    okText: "继续",
                    onOk() {
                        tbOrderShouldDecrypt(order);
                    },
                });

                return [];
            } else {
                tbOrderShouldDecrypt(order);
            }
        } else if (type === DecryptedType.NAME_AND_STREET) {
            if (import.meta.env.VITE_IS_QN) {
                return listDecryptedConsignee({
                    shopId,
                    orders: [order],
                    decryptNameAddressOnly: true,
                });
            } else {
                const decryptor = new TBOrderDecryptor(order);

                const listDecryptedConsigneeResult = await decryptor.decrypt();

                postLogOrderDecryptConsigneeByPlatformApi(shopId, [
                    {
                        id: order.id,
                        originalId: order.originalId,
                        mergeSplitStatus: order.mergeSplitStatus,
                    },
                ]);

                return [listDecryptedConsigneeResult];
            }
        }
    } else {
        if ([ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL, ...apiShopTypes].includes(shop.type)) {
            const { success: required } = await checkDecryptCaptchaRequired(order.shopId);

            if (!required) {
                return listDecryptedConsignee({
                    shopId,
                    orders: [order],
                });
            }

            const ret = await new Promise<{ captchaKey: string; captchaText: string }>((resolve) => {
                globalStore.modalStore.openModal(CaptchaModal, {
                    shopId,
                    onOk: async (captchaKey, captchaText) => {
                        resolve({ captchaKey, captchaText });
                    },
                    onReject: () => {
                        resolve(null);
                    },
                });
            });

            if (!ret) return [];

            const { captchaKey, captchaText } = ret;

            return listDecryptedConsignee({
                shopId,
                orders: [order],
                captchaKey,
                captchaText,
            });
        } else {
            return listDecryptedConsignee({
                shopId,
                orders: [order],
            });
        }
    }
}

export function handleOrderDecryptedConsigneeError(e: any, shopId: number) {
    /** 仅处理网关中的异常 */
    if (e?.type === ShopType.PINDUODUO) {
        if (e.extraInfo?.successful === false) {
            const result = (e.extraInfo as unknown) as PddOrderDecryptResult;

            if (!result) return;

            if (result.isNeedVerify) {
                PddOrderDecryptor.pddVerityConfirm(result.url);
            } else {
                Modal.error({
                    title: "获取拼多多订单信息失败",
                    content: result.message,
                });
            }

            return;
        }
    }

    /** TB */
    if (e?.type === ShopType.TAOBAO) {
        Modal.error({ title: "获取淘宝订单信息失败", content: e.message });

        return;
    }

    if (e?.message?.includes("淘宝订单解密失败，预付款余额不足")) {
        Modal.error({
            title: "淘宝订单解密失败。预付款余额不足。",
            content:
                "淘宝官方解密手机号码接口升级为付费接口，目前需要收费(每单0.1元)。如需查看淘宝订单详细信息或打印非菜鸟面单，请先充值付费后再操作",
            cancelText: "关闭",
            okText: "去充值",
            okCancel: true,
            async onOk() {
                if (import.meta.env.VITE_IS_QN) {
                    const token = await getSETokenFromQn();

                    const a = document.createElement("a");

                    a.href = `${FD_BASE_URL}/setting/shop/prepaidAccount?_token_=${token}&shopId=${shopId}`;

                    a.target = "_blank";

                    a.click();
                } else {
                    window.open(`/setting/shop/prepaidAccount?shopId=${shopId}`);
                }
            },
        });

        return;
    }

    showErrorModal("错误信息", e?.message);
}

/**
 * 解密单个订单
 */
function useSingleOrderDecryptedConsignee() {
    const globalStore = useGlobalStore();

    return useRequest(
        async (order: OrderDTO, type = DecryptedType.ALL) => {
            try {
                const pass = globalStore.checkUserAuthority(Authority.ROLE_ORDER_PRIVACY_CONSIGNEE_DECRYPT);

                if (!pass) return [];

                const results = await singleOrderDecryptedConsignee(order, type, globalStore);

                return results;
            } catch (error) {
                handleOrderDecryptedConsigneeError(error, order.shopId);

                return [];
            }
        },
        {
            manual: true,
        },
    );
}

export default useSingleOrderDecryptedConsignee;
